import { useState } from 'react';
import { Box, Button, Checkbox, Chip, Dialog, DialogActions, DialogContent, DialogTitle, Stack, TableCell, TableRow, Tooltip, Typography } from '@mui/material';
import VisibilityIcon from '@mui/icons-material/Visibility';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import DeleteIcon from '@mui/icons-material/Delete';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import moment from 'moment';
import { dateMonthYearDateFormat } from '../../../constants';
import NumericLabel from 'react-pretty-numbers';
import { trackActivity } from '../../../utils/mixpanel';
import UploadingIcon from '../../../shared_elements/components/UploadingIcon';
import { PermissionWrapper } from '../../../wrappers';
import { displayDateFormatTime } from "../../../constants";
import AuditPopover from '../../../shared_elements/components/popover';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import LessMoreComp from '../../../shared_elements/components/CustomLessMoreComp';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { useSelector } from 'react-redux';
import RestoreIcon from '@mui/icons-material/Restore';
import { Replay as ReplayIcon } from '@mui/icons-material';
import { titanAviationIntance } from '../../../shared_elements';


const StatusLabel = ({ item, onInvStatus, isStatus = true, type }) => {
    const isReserveOrChildInvoice = item?.invoice_type?.value !== 10 && (item?.invoice_type?.value === 1 || (item?.parent_type ===1 && item?.parent_id)) 
    const isReserveOrChildApproved = isReserveOrChildInvoice && item?.approval_status === 'approved'
    // Independent invoices (including Management Fee, type 10, whether standalone or a child of a Reserve invoice) have no control stages - they use the single-decision approval_decision.status instead.
    const isIndependentInvoiceApproved = !isReserveOrChildInvoice && item?.is_invoice_approval && item?.approval_decision?.status === 2
    const showApprovedOnDraft = item?.status?.value === 1 && titanAviationIntance && (isReserveOrChildApproved || isIndependentInvoiceApproved)
    const statusClass = showApprovedOnDraft ? 'invoice-status-approved' : `invoice-status-${item.status.value}`
    const statusLabel = showApprovedOnDraft ? 'Approved' : (item?.status?.label ? item?.status?.label : '-- ')
    const stageStatus = item?.current_stage?.status?.toLowerCase()
    const stageStatusLabel = stageStatus?.startsWith('reject') ? (item?.current_stage?.previous_stage ? `Rejected[${item.current_stage.previous_stage}]` : 'Rejected') : stageStatus?.startsWith('approve') ? 'Approved' : stageStatus ? 'Pending Approval' : null
    return (
        <>
            <Button variant="contained" className={statusClass}
                disabled={!isStatus}
                sx={{
                    textTransform: "capitalize",
                    padding: "0 6px",
                    fontWeight: "600",
                    alignItems: "center",
                    fontSize: "13px",
                    whiteSpace: "nowrap",
                    width: "120px",
                    cursor: isStatus ? "pointer" : "not-allowed",
                    justifyContent: "center",
                    borderRadius: "3px",
                    "& .MuiButton-endIcon": {
                        marginLeft: "0px",
                    },
                    "&.Mui-disabled": { cursor: "not-allowed", opacity: 1, pointerEvents: "auto" },
                }}
                onClick={() => [1, 2, 5, 6].includes(item.status.value) ? onInvStatus() : null}
                endIcon={isStatus && [1, 2, 5, 6].includes(item.status.value) ? <ArrowDropDownIcon /> : null} >{statusLabel}</Button>
            {[5].includes(item?.status?.value) ? <Stack spacing={0}>
                <Typography sx={{ fontSize: "10px" }}><span style={{ fontSize: "12px" }}>Partial Payment:</span> {item?.partial_payment?.partial_payment}</Typography>
                <Typography sx={{ fontSize: "10px" }}><span style={{ fontSize: "12px" }}>Payment Date:</span> {item?.partial_payment?.payment_date ? moment(item?.partial_payment?.payment_date).format(dateMonthYearDateFormat) : "--"}</Typography>
                <Typography sx={{ fontSize: "10px" }}><span style={{ fontSize: "12px" }}>Balance Amount:</span> <NumericLabel params={{ locales: 'en-GB', currency: true, currencyIndicator: item?.currency?.code, precision: 2, commafy: true, justification: 'L' }}>{item?.balance_amount || 0.00}</NumericLabel></Typography>
                {item?.partial_payment?.payment_late_by === "0" ? null : <Typography sx={{ color: "red", fontSize: "10px" }}>Payment late by {Number(item?.partial_payment?.payment_late_by).toLocaleString("en-IN")} Day(s)</Typography>}
                {item?.overdue_by !== null && Number(item?.overdue_by) > 0 ? <Typography sx={{ color: "red", fontSize: "10px" }}>Overdue by {Number(item?.overdue_by).toLocaleString("en-IN")} Day(s)</Typography> : null}
            </Stack> : [2].includes(item?.status?.value) ?
                <Stack spacing={0}>
                    {item?.overdue_by !== null  && Number(item?.overdue_by) > 0 ? <Typography sx={{ color: "red", fontSize: "10px" }}>Overdue by {Number(item?.overdue_by).toLocaleString("en-IN")} Day(s)</Typography> : null}
                </Stack> : [3].includes(item?.status?.value) ?
                    <Stack spacing={0}>
                        <Typography sx={{ fontSize: "10px" }}><span style={{ fontSize: "12px" }}>Payment Date:</span> {item?.payment_date ? moment(item.payment_date).format(dateMonthYearDateFormat) : '--'}</Typography>
                        {item?.payment_late_by !== 0 ? <Typography sx={{ color: "red", fontSize: "10px" }}><span style={{ fontSize: "12px" }}>Payment late by</span> {Number(item?.payment_late_by).toLocaleString("en-IN")} Day(s)</Typography> : null}
                    </Stack> : [1].includes(item?.status?.value) && !showApprovedOnDraft && titanAviationIntance && isReserveOrChildInvoice && item?.current_stage?.stage_name ?
                        <Stack spacing={0}>
                            {stageStatusLabel ? <Typography sx={{ fontSize: "12px", whiteSpace: "nowrap" }}>{stageStatusLabel}</Typography> : null}
                            <Typography sx={{ fontSize: "10px" }}><span style={{ fontSize: "12px" }}>Pending Approval Stage:</span><span style={{ fontSize: "11px" }}> {item?.current_stage?.stage_name}</span></Typography>
                        </Stack> : null}
        </>
    )
}

const ParentInvoice = ({ item, viewFrom }) => {

    if (!item?.parent_slug) return null;

    const handleClick = () => {
        viewFrom?.();
        window.open(`/invoices/view/${item.parent_slug}`, "_blank");
        trackActivity('Item Clicked', { page_title: `Invoice Listing`, page_type: `Parent Invoice ${item?.parent_invoice_number || ""}`, })
    };

    return (
        <Stack spacing={0}>
            <Typography variant="caption" color="primary" sx={{ fontSize: "13px" }} > Parent Invoice: </Typography>
            <Typography
                sx={{ textDecoration: "underline", cursor: "pointer", fontSize: "13px" }}
                variant="caption"
                color="primary"
                onClick={handleClick}
            >
                {item?.parent_invoice_number ? item?.parent_invoice_number : '--'}
            </Typography>
        </Stack>
    );
};

const LinkedSummaryInvoice = ({ item, viewFrom }) => {

    if (!item?.linked_summary_invoices) return null;

    const handleClick = () => {
        viewFrom?.();
        window.open(`/summary-invoices/${item.linked_summary_invoices?.slug}`, "_blank");
        trackActivity('Item Clicked', { page_title: `Invoice Listing`, page_type: `Summary Invoice`, })
    };

    return (
            <Typography
                sx={{ textDecoration: "underline", cursor: "pointer", fontSize: "13px", backgroundColor:"yellow", color:'black' }}
                variant="caption"
                color="primary"
                onClick={handleClick}
            >
                Linked with summary invoice
            </Typography>
    );
};


const InvoiceList = ({ toggleBulkOps, item, type, onDelete, onView, viewFile, bulk, uploadedFileData, slug, viewFileStatus, uploadFileProgressData, viewFrom, onInvStatus, onRevertStatus, viewRevisonHistory, isSelected, canSelect, selectDisabledReason, onToggleSelect }) => {
    const { permission } = useSelector((state) => state.leases);
    const leasePermission = permission?.permissions?.lease_revamp_application?.lease_revamp?.lease_details;
    const status = Array.isArray(leasePermission) ? leasePermission.includes('U') : false;
    const [linkedMsg, setLinkedMsg] = useState('');
    const [managementlinkedMsg, setManagementLinkedMsg] = useState('');
    const isRevertTrue = item?.management_fee_invoice?.id ? [3, 5].includes(item?.status?.value) ? true : false : [2, 3, 5].includes(item?.status?.value) && !item?.is_summary_linked ? true : false;
    const isSummaryLinked = item?.is_summary_linked === true;
    const [invApprovalMsg, setInvApprovalMsg] = useState('');
    const handleItemClicked = (page_type) => {
        trackActivity('Item Clicked', { page_title: `Invoice Listing`, page_type: page_type, })
    }
    const isApprovalApplicable = item?.is_invoice_approval && item?.invoice_type?.value !== 10 && !!item?.lease_slug && item?.status?.value === 1
    const isTitanReserveOrChildInvoice = item?.status?.value === 1 && titanAviationIntance && item?.is_invoice_approval && item?.invoice_type?.value !== 10 && (item?.invoice_type?.value === 1 || (item?.parent_type ===1 && item?.parent_id))
    const isTitanApprovalMet = item?.approval_status === 'approved'
    // Multi Approval Flow (Titan) has no single approval_decision - "approved" only once every control stage is approved (item.approval_status), unlike the single-decision approval_decision.status used elsewhere.
    const isApprovalBlocked = isTitanReserveOrChildInvoice ? !isTitanApprovalMet : (isApprovalApplicable && item?.approval_decision?.status !== 2)
    const checkboxTooltip = isApprovalBlocked
        ? 'This invoice is not approved yet. Selection is not available.'
        : (selectDisabledReason || '')

    const handleInvStatus = () => {
        if (isSummaryLinked) {
            setLinkedMsg('Status cannot be changed because this invoice is currently linked with a Summary Invoice.');
        } else if (isApprovalApplicable && item?.approval_decision?.status !== 2) {
            setInvApprovalMsg('This invoice is not approved yet. Change of status is not available.');
        } else if (isTitanReserveOrChildInvoice && !isTitanApprovalMet) {
            setInvApprovalMsg('This invoice is not approved yet. Change of status is not available.');
        } else {
            onInvStatus();
        }
    };
    return (
        <>
        <TableRow>
            <TableCell className="table-actions-list">
                {parseFloat(item?.offset_excess_amount) < 0 && (
                    <Box
                        sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            width: 0,
                            height: 0,
                            borderStyle: 'solid',
                            borderWidth: '15px 15px 0 0',
                            borderTopColor: 'error.main',
                            borderRightColor: 'transparent',
                            pointerEvents: 'none',
                        }}
                    />
                )}
                <Box className="icon-container" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <Tooltip title={checkboxTooltip} arrow placement="right" disableHoverListener={!checkboxTooltip} disableFocusListener={!checkboxTooltip} disableTouchListener={!checkboxTooltip}>
                        <span onClick={e => e.stopPropagation()}>
                            <Checkbox
                                size="small"
                                checked={canSelect && !!isSelected}
                                disabled={!canSelect || isApprovalBlocked}
                                onChange={canSelect ? onToggleSelect : undefined}
                            />
                        </span>
                    </Tooltip>
                    <AuditPopover item={item}>
                        <MoreVertIcon fontSize="small" color="primary" sx={{ cursor: "pointer" }} />
                    </AuditPopover>
                    <Tooltip title="Revision History">
                        <RestoreIcon className='revision-history' color="primary" fontSize='small' sx={{ cursor: 'pointer' }} onClick={() => { viewRevisonHistory(); handleItemClicked('RevisionHistoryIcon') }} />
                    </Tooltip>
                    <PermissionWrapper extraProps={{ application: "lease_revamp", feature: "invoices", operationType: ['R'] }}>
                        <Tooltip title="View" arrow>
                            <VisibilityIcon fontSize="small" onClick={() => { onView(); handleItemClicked('ViewIcon') }} color="primary" sx={{ cursor: "pointer" }} />
                        </Tooltip>
                    </PermissionWrapper>
                    {item?.is_settle !== true && [1, 2, 4].includes(item?.status.value) && !item?.qb_last_sync_by  ? <PermissionWrapper extraProps={{ application: "lease_revamp", feature: "invoices", operationType: ['D'] }}>
                        <Tooltip arrow title="Delete">
                            <DeleteIcon
                                fontSize="small"
                                color="error"
                                sx={{ cursor: 'pointer' }}
                                onClick={() => {
                                    if (isSummaryLinked) {
                                        setLinkedMsg('This invoice cannot be deleted because it is linked with a Summary Invoice.');
                                    } else if (item?.management_fee_invoice?.slug) {
                                        setManagementLinkedMsg('This invoice cannot be deleted because it is linked with a Management Fee Invoice.');
                                    } else {
                                        onDelete();
                                        handleItemClicked('DeleteIcon');
                                    }
                                }}
                            />
                        </Tooltip>
                    </PermissionWrapper> : null}
                    {isRevertTrue && !item?.qb_last_sync_by?
                        <PermissionWrapper extraProps={{ application: "lease_revamp", feature: "invoices", operationType: ['U'] }}>
                            <Tooltip title={'Revert Status'}>
                                <ReplayIcon fontSize="small" color={ 'primary'} onClick={() => { if (isSummaryLinked) { setLinkedMsg('This invoice cannot be reverted because it is linked with a Summary Invoice.'); return; } onRevertStatus?.(); handleItemClicked('RevertIcon') }} sx={{ cursor: isSummaryLinked ? 'not-allowed' : 'pointer' }} />
                            </Tooltip>
                        </PermissionWrapper> : null}
                    <PermissionWrapper extraProps={{ application: "lease_revamp", feature: "invoices", operationType: ['R'] }}>
                                <Tooltip title="Attached File(s)" arrow>
                                    <Box className="attachment-container">
                                        <AttachFileIcon fontSize="small" color="primary" onClick={() => { viewFile(); handleItemClicked('AttachmentsIcon') }} sx={{ cursor: "pointer" }} />
                                        <Box className="attachment-count">{item?.attachments?.length > 0 ? `(${item?.attachments?.length})` : null} </Box>
                                    </Box>
                                </Tooltip>
                    </PermissionWrapper>
                    {(slug === item?.slug) && <UploadingIcon viewFileStatus={() => viewFileStatus(uploadedFileData)} />}
                </Box>
            </TableCell>
            {type !== 'leaseInvoice' ? <TableCell sx={{ cursor: 'pointer' }} onClick={() => { onView(); handleItemClicked('Clicked on Table data') }}>
                {item?.asset?.name || '--'}
                {item?.qb_last_sync_by && (<div> <Chip sx={{ fontSize: '10px', height: '18px' }} label="QB Synced" size="small" color="success" variant="outlined" /> </div>)}
            </TableCell> : null}
            <TableCell sx={{ cursor: 'pointer' }} onClick={() => { onView(); handleItemClicked('Clicked on Table data'); }} >
                {item?.invoice_number || '--'}
                <div style={{ fontSize: '10px', height: '18px' }}> {item?.invoice_type?.label || '--'}{' '} {(item?.parent_id && item?.invoice_type?.value !== 3) || item?.reconcile_invoice && [4, 5].includes(item?.invoice_type?.value) ? ' Note' : ''} </div>
                <div style={{ fontSize: '10px' }}> <ParentInvoice item={item} /> </div>
                {item?.reconcile_invoice && (<div> <Chip sx={{ fontSize: '10px', height: '18px' }} label="Reconcile" size="small" variant="outlined" /> </div>)}
                {item?.is_advanced && (<div> <Chip sx={{ fontSize: '10px', height: '18px' }} label="Advanced" size="small" variant="outlined" /> </div>)}
                {item?.is_arrears && (<div> <Chip sx={{ fontSize: '10px', height: '18px' }} label="Arrears" size="small" variant="outlined" /> </div>)}
                {item?.child_invoice_type === 1 || item?.linked_invoice_type === 1 && (<div> <Chip sx={{ fontSize: '10px', height: '18px' }} label="Reserve" size="small" variant="outlined" /> </div>)}
                {item?.child_invoice_type === 2 || item?.linked_invoice_type === 2 && (<div> <Chip sx={{ fontSize: '10px', height: '18px' }} label="Rental" size="small" variant="outlined" /> </div>)}
                {type === 'leaseInvoice' && item?.qb_last_sync_by && (<div> <Chip sx={{ fontSize: '10px', height: '18px' }} label="QB Synced" size="small" color="success" variant="outlined" /> </div>)}
                <div style={{ fontSize: '10px' }}> <LinkedSummaryInvoice item={item} /> </div>
            </TableCell>
            {type !== 'leaseInvoice' ? <TableCell sx={{ cursor: 'pointer' }} onClick={() => { onView(); handleItemClicked('Clicked on Table data') }}><div style={{ width: item?.lessor?.name?.length > 20 ? "200px" : '' }}><LessMoreComp name={item?.lessor?.name ? item.lessor?.name : "--"} lessMr='Lessor' /></div></TableCell> : null}
            {type !== 'leaseInvoice' ? <TableCell sx={{ cursor: 'pointer' }} onClick={() => { onView(); handleItemClicked('Clicked on Table data') }}><div style={{ width: item?.lessee?.name?.length > 20 ? "200px" : '' }}><LessMoreComp name={item?.lessee?.name ? item.lessee?.name : "--"} lessMr='lessee' /></div></TableCell> : null}
            <TableCell sx={{ cursor: 'pointer' }} onClick={() => { onView(); handleItemClicked('Clicked on Table data') }}>{item?.date ? moment(item.date).format(dateMonthYearDateFormat) : '--'}</TableCell>
            <TableCell sx={{ cursor: 'pointer' }} onClick={() => { onView(); handleItemClicked('Clicked on Table data') }}>{item?.due_date ? moment(item.due_date).format(dateMonthYearDateFormat) : '--'}</TableCell>
            <TableCell sx={{ cursor: 'pointer' }} onClick={() => { onView(); handleItemClicked('Clicked on Table data') }}>{item?.invoice_period ? item.invoice_period.split(' - ').map(d => moment(d).format(dateMonthYearDateFormat)).join(' - ') : '__'} </TableCell>
            <TableCell>{<StatusLabel item={item} onInvStatus={handleInvStatus} isStatus={status && !item?.qb_last_sync_by}  type={type} />}</TableCell>
            <TableCell sx={{ cursor: 'pointer' }} onClick={() => { onView(); handleItemClicked('Clicked on Table data') }}>
                <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                    <NumericLabel params={{ locales: 'en-GB', currency: true, currencyIndicator: item?.currency?.code, precision: 2, commafy: true, justification: 'L' }}>{item?.amount}</NumericLabel>
                    {item?.excess_amount > 0 && (
                        <Tooltip
                            title={<>Excess Amount: <NumericLabel params={{ locales: 'en-GB', currency: true, currencyIndicator: item?.currency?.code, precision: 2, commafy: true, justification: 'L' }}>{item?.excess_amount}</NumericLabel></>}
                            arrow
                        >
                            <InfoOutlinedIcon sx={{ fontSize: '15px', color: '#64748b', cursor: 'pointer' }} />
                        </Tooltip>
                    )}
                </Box>
            </TableCell>
        </TableRow>
        {linkedMsg &&
            <tr style={{ display: 'none' }}>
                <td>
                    <Dialog open={true} maxWidth='sm' fullWidth className='add-lease-dialog'>
                        <DialogTitle sx={{ m: 0, p: 2 }} className='dialog-header'>Action Restricted</DialogTitle>
                        <DialogContent dividers>
                            <Typography sx={{ fontSize: 14 }}>{linkedMsg}</Typography>
                        </DialogContent>
                        <DialogActions>
                            <Button className='cancel-btn' variant='outlined' size='small' onClick={() => { setLinkedMsg(''); handleItemClicked('Close Button') }}>Close</Button>
                        </DialogActions>
                    </Dialog>
                </td>
            </tr>
        }
            {managementlinkedMsg &&
                <tr style={{ display: 'none' }}>
                    <td>
                        <Dialog open={true} maxWidth='sm' fullWidth className='add-lease-dialog'>
                            <DialogTitle sx={{ m: 0, p: 2 }} className='dialog-header'>Action Restricted</DialogTitle>
                            <DialogContent dividers>
                                <Typography sx={{ fontSize: 14 }}>{managementlinkedMsg}</Typography>
                            </DialogContent>
                            <DialogActions>
                                <Button className='cancel-btn' variant='outlined' size='small' onClick={() => { setManagementLinkedMsg(''); handleItemClicked('Close Button') }}>Close</Button>
                            </DialogActions>
                        </Dialog>
                    </td>
                </tr>
            }
            {invApprovalMsg &&
                <Dialog open maxWidth='sm' fullWidth>
                    <DialogTitle sx={{ m: 0, p: 2 }}>Action Restricted</DialogTitle>
                    <DialogContent dividers>
                        <Typography sx={{ fontSize: 14 }}>{invApprovalMsg}</Typography>
                    </DialogContent>
                    <DialogActions>
                        <Button className='cancel-btn' variant='outlined' size='small' onClick={() => { setInvApprovalMsg(''); handleItemClicked('Close Button') }}>Close</Button>
                    </DialogActions>
                </Dialog>
            }
        </>
    );
};

export default InvoiceList;
